import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@pilot-hub/api-client'
import { useEinsatz, useEinsatzWechsel, useBestaetigung, useConnectionRetry } from '@pilot-hub/ui'
import { MapContainer, Marker, Polyline, Polygon, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { GrundkarteLayer, GrundkarteUmschalter, useGrundkarte } from '../components/GrundkarteWahl'
import 'leaflet/dist/leaflet.css'
import { Save, Download, Send, Trash2, Undo2, Eraser, Plus, MapPin } from 'lucide-react'
import {
  berechneRoute, eingabeArt, routenAktion, fotoAbstand, spurabstand, abstandM, STANDARD_PARAMETER, KAMERAS,
  type Geometrie, type LatLon, type Linse, type Muster, type RoutenParameter, type RoutenTyp, type Seite,
} from '../utils/routenplanung'

// Missionsplaner für den Co-Piloten (Tablet quer): Karte links, Seitenleiste rechts mit Routentyp, Parametern,
// berechneten Werten und Prüfung vor dem Senden. Routen werden je Einsatz gespeichert (Pilot-API
// /missionsrouten) und als DJI-Routendatei (KMZ) heruntergeladen oder über das Wayline-Modul der Cloud API
// direkt in die Routenbibliothek von DJI Pilot 2 gestellt (PUT/DELETE /missionsrouten/{id}/pilot2).

interface Route {
  id: number
  einsatz_id: number | null
  name: string
  typ: RoutenTyp
  muster: Muster | null
  geometrie: Partial<Geometrie>
  parameter: Partial<RoutenParameter>
  pruefung: Partial<Pruefung>
  aktualisiert_am: string | null
  erstellt_von: string | null
  pilot2_gesendet_am?: string | null
}

interface Pruefung { luftraum: boolean; hindernisse: boolean; wetter: boolean }
interface Suchgebiet { id: string; kennung: string; punkte: [number, number][] }

const TYPEN: { key: RoutenTyp; label: string }[] = [
  { key: 'WEGPUNKTE', label: 'Wegpunkte' }, { key: 'FLAECHE', label: 'Fläche' },
  { key: 'LINIE', label: 'Linie' }, { key: 'SUCHMUSTER', label: 'Suchmuster' },
]
const MUSTER: { key: Muster; label: string }[] = [
  { key: 'PARALLEL', label: 'Parallel' }, { key: 'EXPANDING_SQUARE', label: 'Expanding Square' },
  { key: 'SEKTOR', label: 'Sektor' }, { key: 'UFERLINIE', label: 'Uferlinie' },
]
const LINSEN: { key: Linse; label: string }[] = [{ key: 'wide', label: 'Weit' }, { key: 'zoom', label: 'Zoom' }, { key: 'ir', label: 'IR' }]
const LEER: Geometrie = { punkte: [], mittelpunkt: null, startplatz: null }
const PRUEFUNG_LEER: Pruefung = { luftraum: false, hindernisse: false, wetter: false }

// Geozonen (DIPUL, §21h LuftVO) als ein WMS-Overlay
const DIPUL_OWS = 'https://uas-betrieb.de/geoservices/dipul/ows'
const DIPUL_LAYER = 'flugbeschraenkungsgebiete,temporaere_betriebseinschraenkungen,kontrollzonen,flughaefen,flugplaetze'

function Geozonen() {
  const map = useMap()
  useEffect(() => {
    const wms = L.tileLayer.wms(DIPUL_OWS, {
      layers: DIPUL_LAYER, styles: '', format: 'image/png', transparent: true, version: '1.1.1', opacity: 0.6,
      attribution: 'Geozonen: &copy; DFS/DIPUL', crossOrigin: true,
    })
    wms.addTo(map)
    return () => { map.removeLayer(wms) }
  }, [map])
  return null
}

function KartenKlick({ onKlick }: { onKlick: (p: LatLon) => void }) {
  useMapEvents({ click: e => onKlick([e.latlng.lat, e.latlng.lng]) })
  return null
}

function Zentrieren({ ziel }: { ziel: LatLon | null }) {
  const map = useMap()
  useEffect(() => { if (ziel) map.setView(ziel, Math.max(map.getZoom(), 16)) }, [ziel, map])
  return null
}

const punktIcon = (text: string, farbe: string) => L.divIcon({
  className: '', iconSize: [26, 26], iconAnchor: [13, 13],
  html: `<div style="width:26px;height:26px;border-radius:50%;background:${farbe};border:2px solid #fff;color:#fff;font:600 11px/22px system-ui;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.5)">${text}</div>`,
})
const startIcon = L.divIcon({
  className: '', iconSize: [30, 30], iconAnchor: [15, 15],
  html: '<div style="width:30px;height:30px;background:#fff;border:3px solid #16a34a;color:#16a34a;font:700 12px/24px system-ui;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.5)">S</div>',
})
const mitteIcon = L.divIcon({
  className: '', iconSize: [34, 34], iconAnchor: [17, 17],
  html: '<div style="width:34px;height:34px;border-radius:50%;border:3px solid #f97316;background:rgba(249,115,22,.25);box-shadow:0 1px 3px rgba(0,0,0,.5)"></div>',
})

const zahl = (v: number | null | undefined, stellen = 0) =>
  v === null || v === undefined ? '–' : v.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen })

/* ------------------------------------------------------------- kleine Bausteine */

function Chip({ aktiv, onClick, children }: { aktiv: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={aktiv}
      className={`min-h-[48px] rounded-lg border px-3 text-sm font-medium transition-colors ${aktiv
        ? 'border-primary-500 bg-primary-500 text-white' : 'border-dark-border bg-dark-bg-tertiary text-dark-text hover:border-primary-500/60'}`}>
      {children}
    </button>
  )
}

function Knopf({ children, onClick, gesperrt, primaer, gefahr, titel }: {
  children: React.ReactNode; onClick?: () => void; gesperrt?: boolean; primaer?: boolean; gefahr?: boolean; titel?: string
}) {
  return (
    <button type="button" onClick={onClick} disabled={gesperrt} title={titel}
      className={`flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        primaer ? 'border-primary-500 bg-primary-500 text-white hover:bg-primary-600'
          : gefahr ? 'border-red-500/60 text-red-300 hover:bg-red-500/10'
            : 'border-dark-border bg-dark-bg-tertiary text-dark-text hover:border-primary-500/60'}`}>
      {children}
    </button>
  )
}

const Titel = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-dark-text-subtle">{children}</div>
)

// Geschwindigkeit: Eingabe/Anzeige in km/h, gespeichert und an DJI (WPML autoFlightSpeed) in m/s. DJI-Grenzen 1–15 m/s.
const TEMPO_MIN_MS = 1
const TEMPO_MAX_MS = 15
const TEMPO_MIN_KMH = TEMPO_MIN_MS * 3.6
const TEMPO_MAX_KMH = TEMPO_MAX_MS * 3.6
const ms_zu_kmh = (ms: number) => Math.round(ms * 3.6 * 10) / 10

function Zahlfeld({ id, label, wert, einheit, onChange, platzhalter, min, max, schritt = 1 }: {
  id: string; label: string; wert: number | null; einheit?: string; onChange: (v: number | null) => void
  platzhalter?: string; min?: number; max?: number; schritt?: number
}) {
  return (
    <label htmlFor={id} className="grid grid-cols-[1fr_96px] items-center gap-2 text-sm text-dark-text-muted">
      <span>{label}</span>
      <span className="relative">
        <input id={id} type="number" inputMode="decimal" min={min} max={max} step={schritt} value={wert ?? ''} placeholder={platzhalter}
          onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className="h-12 w-full rounded-lg border border-dark-border bg-dark-bg px-2 pr-8 text-right text-sm text-dark-text tabular-nums" />
        {einheit && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-dark-text-subtle">{einheit}</span>}
      </span>
    </label>
  )
}

/* ------------------------------------------------------------- Seite */

export function CoPilotMissionsplanerPage() {
  const [grundkarte, setGrundkarte] = useGrundkarte('pilot-missionsplaner-grundkarte')
  // Einsatz (optional) aus der globalen Einsatzwahl; Co-Pilot: Einsatz aus dem Kräfte Management
  const { einsatz: ew } = useEinsatz()
  const [einsatz, setEinsatz] = useState<{ id: number; titel: string; ort: LatLon | null } | null>(null)
  const [suchgebiete, setSuchgebiete] = useState<Suchgebiet[]>([])
  const [routen, setRouten] = useState<Route[]>([])
  const [routeId, setRouteId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [typ, setTyp] = useState<RoutenTyp>('SUCHMUSTER')
  const [muster, setMuster] = useState<Muster | null>('PARALLEL')
  const [geo, setGeo] = useState<Geometrie>(LEER)
  const [par, setPar] = useState<RoutenParameter>(STANDARD_PARAMETER)
  const [pruefung, setPruefung] = useState<Pruefung>(PRUEFUNG_LEER)
  const [modus, setModus] = useState<'eingabe' | 'startplatz'>('eingabe')
  const [geaendert, setGeaendert] = useState(false)
  const [speichert, setSpeichert] = useState(false)
  const [meldung, setMeldung] = useState<{ text: string; fehler?: boolean } | null>(null)
  const [loeschenFrage, setLoeschenFrage] = useState(false)
  const [pilot2Gesendet, setPilot2Gesendet] = useState<string | null>(null)
  const [pilot2Laeuft, setPilot2Laeuft] = useState(false)
  const [geozonen, setGeozonen] = useState(true)
  const [zentrum, setZentrum] = useState<LatLon | null>(null)

  const aendern = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setGeaendert(true); setMeldung(null) }
  const setP = (teil: Partial<RoutenParameter>) => { setPar(p => ({ ...p, ...teil })); setGeaendert(true); setMeldung(null) }

  /* --- Einsatz, Suchgebiete, Routen laden */
  useEffect(() => {
    if (!ew) { setEinsatz(null); return }
    const e = ew.raw ?? {}
    const ort: LatLon | null = e.einsatzort_lat && e.einsatzort_lon ? [e.einsatzort_lat, e.einsatzort_lon] : null
    setEinsatz({ id: ew.id, titel: ew.titel, ort })
    if (ort) setZentrum(ort)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ew?.id])

  const routenLaden = useCallback(() => {
    api.get('/missionsrouten', { params: einsatz ? { einsatz_id: einsatz.id } : {} })
      .then(r => setRouten(r.data as Route[])).catch(() => setMeldung({ text: 'Routen konnten nicht geladen werden.', fehler: true }))
  }, [einsatz])
  useEffect(() => { routenLaden() }, [routenLaden])

  useEffect(() => {
    if (!einsatz) { setSuchgebiete([]); return }
    api.get(`/taktische-karte/${einsatz.id}`)
      .then(r => setSuchgebiete(Array.isArray((r.data as any)?.data?.suchgebiete) ? (r.data as any).data.suchgebiete : []))
      .catch(() => {
        // Suchgebiete fehlen nur im Hintergrund der Planung; sichtbar machen statt "keine Suchgebiete" vorzutäuschen
        setSuchgebiete([])
        setMeldung({ text: 'Suchgebiete des Einsatzes konnten nicht geladen werden.', fehler: true })
      })
  }, [einsatz])
  useConnectionRetry(() => { if (einsatz) setEinsatz({ ...einsatz }); else routenLaden() })
  // ungespeicherte Route: Wechsel nur nach Rückfrage
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung()
  useEinsatzWechsel(() => !geaendert || bestaetigen({ titel: 'Ungespeicherte Änderungen', text: 'Die Route hat ungespeicherte Änderungen. Einsatz trotzdem wechseln?', bestaetigenText: 'Trotzdem wechseln', abbrechenText: 'Hier bleiben' }))

  /* --- Route wählen / neu */
  function neueRoute() {
    setRouteId(null); setName(''); setTyp('SUCHMUSTER'); setMuster('PARALLEL'); setGeo(LEER)
    setPar(STANDARD_PARAMETER); setPruefung(PRUEFUNG_LEER); setGeaendert(false); setMeldung(null); setLoeschenFrage(false)
    setPilot2Gesendet(null)
  }
  function routeOeffnen(r: Route) {
    setRouteId(r.id); setName(r.name); setTyp(r.typ); setMuster(r.muster)
    setGeo({ ...LEER, ...r.geometrie }); setPar({ ...STANDARD_PARAMETER, ...r.parameter })
    setPruefung({ ...PRUEFUNG_LEER, ...r.pruefung }); setGeaendert(false); setMeldung(null); setLoeschenFrage(false)
    setPilot2Gesendet(r.pilot2_gesendet_am ?? null)
    const z = r.geometrie.mittelpunkt ?? r.geometrie.punkte?.[0] ?? null
    if (z) setZentrum([...z] as LatLon)
  }

  /* --- Berechnung */
  const art = eingabeArt(typ, muster)
  const { wegpunkte, kennzahlen } = useMemo(() => berechneRoute(typ, muster, geo, par), [typ, muster, geo, par])

  /* --- Karte bedienen */
  function kartenKlick(p: LatLon) {
    if (modus === 'startplatz') { aendern(setGeo)({ ...geo, startplatz: p }); setModus('eingabe'); return }
    if (art === 'mittelpunkt') aendern(setGeo)({ ...geo, mittelpunkt: p })
    else aendern(setGeo)({ ...geo, punkte: [...geo.punkte, p] })
  }
  const punktVerschieben = (i: number, p: LatLon) => aendern(setGeo)({ ...geo, punkte: geo.punkte.map((q, k) => (k === i ? p : q)) })
  function suchgebietUebernehmen(id: string) {
    const sg = suchgebiete.find(s => s.id === id)
    if (!sg) return
    aendern(setGeo)({ ...geo, punkte: sg.punkte.map(p => [p[0], p[1]] as LatLon) })
    if (!name) setName(`${sg.kennung} · ${typ === 'FLAECHE' ? 'Fläche' : 'Parallelsuche'}`)
    setZentrum([sg.punkte[0][0], sg.punkte[0][1]])
  }

  /* --- Prüfung */
  const autoPruefung = [
    { ok: wegpunkte.length >= 2, text: wegpunkte.length >= 2 ? `${wegpunkte.length} Wegpunkte berechnet` : 'Route auf der Karte zeichnen' },
    { ok: par.hoehe_m <= 120, text: par.hoehe_m <= 120 ? `Höhe ${zahl(par.hoehe_m)} m über Start (max. 120 m)` : `Höhe ${zahl(par.hoehe_m)} m über 120 m – Genehmigung nötig` },
    { ok: !!kennzahlen && kennzahlen.akkus === 1, text: !kennzahlen ? 'Akkubedarf noch offen' : kennzahlen.akkus === 1 ? 'Ein Akku reicht' : `${kennzahlen.akkus} Akkus nötig – Route teilen oder Akkuwechsel einplanen` },
    { ok: !!geo.startplatz && (kennzahlen?.max_entfernung_m ?? 0) <= 500,
      text: !geo.startplatz ? 'Startplatz setzen' : `Max. Entfernung zum Startplatz ${zahl(kennzahlen?.max_entfernung_m)} m${(kennzahlen?.max_entfernung_m ?? 0) > 500 ? ' – Sichtverbindung prüfen (VLOS/EVLOS)' : ''}` },
  ]
  const manuellFertig = pruefung.luftraum && pruefung.hindernisse && pruefung.wetter

  /* --- Speichern, KMZ, Löschen */
  async function speichern() {
    if (!name.trim()) { setMeldung({ text: 'Bitte einen Namen für die Route angeben.', fehler: true }); return }
    setSpeichert(true); setMeldung(null)
    const daten = {
      einsatz_id: einsatz?.id ?? null, name, typ, muster: typ === 'SUCHMUSTER' ? muster : null,
      geometrie: geo,
      parameter: { ...par, aktion: routenAktion(typ), foto_abstand_m: Math.round(fotoAbstand(par) * 10) / 10 },
      wegpunkte, kennzahlen: kennzahlen ?? {}, pruefung,
    }
    try {
      const r = routeId ? await api.put(`/missionsrouten/${routeId}`, daten) : await api.post('/missionsrouten', daten)
      setRouteId((r.data as Route).id); setPilot2Gesendet((r.data as Route).pilot2_gesendet_am ?? null); setGeaendert(false); setMeldung({ text: 'Route gespeichert.' }); routenLaden()
    } catch (e: any) {
      setMeldung({ text: e?.response?.data?.detail || 'Speichern fehlgeschlagen', fehler: true })
    } finally { setSpeichert(false) }
  }

  async function kmzLaden() {
    if (!routeId) return
    try {
      const r = await api.get(`/missionsrouten/${routeId}/kmz`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${name.replace(/[^\p{L}\p{N}_-]+/gu, '_').slice(0, 60) || 'Route'}.kmz`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
      setMeldung({ text: 'KMZ heruntergeladen. In DJI Pilot 2 über Route → Importieren öffnen.' })
    } catch {
      setMeldung({ text: 'KMZ konnte nicht erzeugt werden.', fehler: true })
    }
  }

  async function anPilot2(senden: boolean) {
    if (!routeId) return
    setPilot2Laeuft(true); setMeldung(null)
    try {
      const r = senden ? await api.put(`/missionsrouten/${routeId}/pilot2`) : await api.delete(`/missionsrouten/${routeId}/pilot2`)
      setPilot2Gesendet((r.data as Route).pilot2_gesendet_am ?? null)
      setMeldung({ text: senden
        ? 'Route ist in der Routenbibliothek freigegeben. In DJI Pilot 2: Route → Cloud-Routen, Liste ggf. neu laden.'
        : 'Route wurde aus der Routenbibliothek von DJI Pilot 2 genommen.' })
      routenLaden()
    } catch (e: any) {
      setMeldung({ text: e?.response?.data?.detail || (senden ? 'Senden fehlgeschlagen.' : 'Zurückziehen fehlgeschlagen.'), fehler: true })
    } finally { setPilot2Laeuft(false) }
  }

  async function loeschen() {
    if (!routeId) return
    try {
      await api.delete(`/missionsrouten/${routeId}`)
      neueRoute(); routenLaden(); setMeldung({ text: 'Route gelöscht.' })
    } catch { setMeldung({ text: 'Löschen fehlgeschlagen.', fehler: true }) }
  }

  const hinweis = modus === 'startplatz' ? 'Startplatz der Drohne auf der Karte antippen'
    : art === 'flaeche' ? 'Ecken der Fläche antippen (oder Suchgebiet übernehmen); Punkte lassen sich ziehen'
      : art === 'linie' ? 'Linie entlang Fluss, Ufer oder Straße antippen; Punkte lassen sich ziehen'
        : art === 'mittelpunkt' ? 'Bezugspunkt (letzte bekannte Position) antippen'
          : 'Wegpunkte der Reihe nach antippen; Punkte lassen sich ziehen'

  const kmzBereit = !!routeId && !geaendert && wegpunkte.length >= 2 && manuellFertig
  const kartenMitte: LatLon = zentrum ?? einsatz?.ort ?? [50.1109, 8.6821]

  return (
    <div className="-m-2 flex ui-fill min-h-[560px] flex-col overflow-hidden rounded-xl border border-dark-border bg-dark-bg sm:-m-3">
      {bestaetigungsDialog}
      {/* Kopfleiste */}
      <div className="flex min-h-[48px] flex-wrap items-center gap-x-3 gap-y-1 border-b border-dark-border bg-dark-bg-secondary px-3 py-1.5 text-sm">
        <span className="font-semibold text-dark-text">Missionsplaner</span>
        <span className="text-dark-text-muted">{einsatz ? einsatz.titel : 'ohne Einsatz'}</span>
        <span className="flex-1" />
        <select id="route-wahl" value={routeId ?? ''} onChange={e => { const r = routen.find(x => x.id === Number(e.target.value)); if (r) routeOeffnen(r) }}
          className="h-12 max-w-[260px] rounded-lg border border-dark-border bg-dark-bg px-2 text-sm text-dark-text">
          <option value="" disabled>{routen.length ? `Gespeicherte Routen (${routen.length})` : 'Keine gespeicherten Routen'}</option>
          {routen.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <button type="button" onClick={neueRoute} className="flex h-12 items-center gap-1 rounded-lg border border-dark-border px-3 text-sm text-dark-text hover:border-primary-500/60">
          <Plus className="h-4 w-4" /> Neu
        </button>
        <span className={`text-xs ${geaendert ? 'text-amber-300' : 'text-dark-text-subtle'}`}>{geaendert ? 'nicht gespeichert' : routeId ? 'gespeichert' : 'Entwurf'}</span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Karte */}
        <div className="relative min-h-[300px] flex-1">
          <MapContainer center={kartenMitte} zoom={16} className="h-full w-full" doubleClickZoom={false}>
            <GrundkarteLayer grundkarte={grundkarte} />
            {geozonen && <Geozonen />}
            <KartenKlick onKlick={kartenKlick} />
            <Zentrieren ziel={zentrum} />
            {suchgebiete.map(sg => (
              <Polygon key={sg.id} positions={sg.punkte} pathOptions={{ color: '#60a5fa', weight: 1.5, dashArray: '6 6', fillOpacity: 0.05 }}>
                <Tooltip sticky>{sg.kennung}</Tooltip>
              </Polygon>
            ))}
            {art === 'flaeche' && geo.punkte.length >= 3 && <Polygon positions={geo.punkte} pathOptions={{ color: '#5fa0ff', weight: 2, fillOpacity: 0.12 }} />}
            {art === 'linie' && geo.punkte.length >= 2 && <Polyline positions={geo.punkte} pathOptions={{ color: '#5fa0ff', weight: 3, dashArray: '4 6' }} />}
            {wegpunkte.length >= 2 && <>
              <Polyline positions={wegpunkte} pathOptions={{ color: '#ffffff', weight: 5, opacity: 0.8 }} />
              <Polyline positions={wegpunkte} pathOptions={{ color: '#f97316', weight: 2.5 }} />
              <CircleMarker center={wegpunkte[0]} radius={7} pathOptions={{ color: '#fff', weight: 2, fillColor: '#16a34a', fillOpacity: 1 }}><Tooltip>Routenbeginn</Tooltip></CircleMarker>
              <CircleMarker center={wegpunkte[wegpunkte.length - 1]} radius={7} pathOptions={{ color: '#fff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}><Tooltip>Routenende</Tooltip></CircleMarker>
            </>}
            {art !== 'mittelpunkt' && geo.punkte.map((p, i) => (
              <Marker key={i} position={p} draggable icon={punktIcon(String(i + 1), '#2563eb')}
                eventHandlers={{ dragend: e => { const ll = (e.target as L.Marker).getLatLng(); punktVerschieben(i, [ll.lat, ll.lng]) } }} />
            ))}
            {art === 'mittelpunkt' && geo.mittelpunkt && (
              <Marker position={geo.mittelpunkt} draggable icon={mitteIcon}
                eventHandlers={{ dragend: e => { const ll = (e.target as L.Marker).getLatLng(); aendern(setGeo)({ ...geo, mittelpunkt: [ll.lat, ll.lng] }) } }}>
                <Tooltip>Bezugspunkt</Tooltip>
              </Marker>
            )}
            {geo.startplatz && (
              <Marker position={geo.startplatz} draggable icon={startIcon}
                eventHandlers={{ dragend: e => { const ll = (e.target as L.Marker).getLatLng(); aendern(setGeo)({ ...geo, startplatz: [ll.lat, ll.lng] }) } }}>
                <Tooltip>Startplatz</Tooltip>
              </Marker>
            )}
          </MapContainer>
          <GrundkarteUmschalter grundkarte={grundkarte} onChange={setGrundkarte} className="absolute bottom-12 left-3 z-20" />
          <div className="pointer-events-none absolute left-14 top-3 z-20 max-w-[65%] rounded-lg bg-black/65 px-3 py-1.5 text-xs text-white">{hinweis}</div>
          {kennzahlen && (
            <div className="pointer-events-none absolute bottom-3 left-3 z-20 rounded-lg bg-black/65 px-3 py-1.5 text-xs text-white">
              Spurabstand {zahl(kennzahlen.abstand_m, 0)} m · Höhe {zahl(par.hoehe_m)} m über Start
            </div>
          )}
          <div className="absolute bottom-3 right-3 z-20 flex gap-2">
            <button type="button" onClick={() => setGeozonen(g => !g)} aria-pressed={geozonen}
              className={`min-h-[48px] rounded-lg border px-3 text-xs font-semibold ${geozonen ? 'border-primary-500 bg-primary-500 text-white' : 'border-white/30 bg-black/60 text-white'}`}>
              Geozonen
            </button>
          </div>
        </div>

        {/* Seitenleiste */}
        <div className="flex max-h-[50%] w-full shrink-0 flex-col gap-3 overflow-y-auto border-t border-dark-border bg-dark-bg-secondary p-3 md:max-h-none md:w-[340px] md:border-l md:border-t-0">
          <label htmlFor="route-name" className="space-y-1">
            <Titel>Name der Route</Titel>
            <input id="route-name" value={name} onChange={e => aendern(setName)(e.target.value)} placeholder="z. B. Suchgebiet 2 · Parallelsuche IR"
              className="h-11 w-full rounded-lg border border-dark-border bg-dark-bg px-3 text-sm text-dark-text" />
          </label>

          <div className="space-y-1.5">
            <Titel>Routentyp</Titel>
            <div className="flex flex-wrap gap-1.5">
              {TYPEN.map(t => <Chip key={t.key} aktiv={typ === t.key} onClick={() => { if (t.key === typ) return; aendern(setTyp)(t.key); if (t.key === 'FLAECHE') setP({ ueberlappung_seite: 60, linsen: ['wide'] }); else if (typ === 'FLAECHE') setP({ ueberlappung_seite: 20, linsen: ['ir', 'wide'] }) }}>{t.label}</Chip>)}
            </div>
            {typ === 'SUCHMUSTER' && (
              <div className="flex flex-wrap gap-1.5">
                {MUSTER.map(m => <Chip key={m.key} aktiv={muster === m.key} onClick={() => aendern(setMuster)(m.key)}>{m.label}</Chip>)}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Titel>Eingabe</Titel>
            <div className="grid grid-cols-2 gap-1.5">
              <Knopf onClick={() => setModus(m => (m === 'startplatz' ? 'eingabe' : 'startplatz'))} primaer={modus === 'startplatz'}>
                <MapPin className="h-4 w-4" /> Startplatz
              </Knopf>
              <Knopf gesperrt={art === 'mittelpunkt' ? !geo.mittelpunkt : !geo.punkte.length}
                onClick={() => aendern(setGeo)(art === 'mittelpunkt' ? { ...geo, mittelpunkt: null } : { ...geo, punkte: geo.punkte.slice(0, -1) })}>
                <Undo2 className="h-4 w-4" /> Zurück
              </Knopf>
              <Knopf gesperrt={!geo.punkte.length && !geo.mittelpunkt && !geo.startplatz} onClick={() => aendern(setGeo)(LEER)}>
                <Eraser className="h-4 w-4" /> Leeren
              </Knopf>
            </div>
            {art === 'flaeche' && suchgebiete.length > 0 && (
              <select id="suchgebiet-wahl" value="" onChange={e => suchgebietUebernehmen(e.target.value)}
                className="h-11 w-full rounded-lg border border-dark-border bg-dark-bg px-2 text-sm text-dark-text">
                <option value="">Suchgebiet aus der taktischen Karte übernehmen …</option>
                {suchgebiete.map(sg => <option key={sg.id} value={sg.id}>{sg.kennung}</option>)}
              </select>
            )}
          </div>

          <div className="space-y-2">
            <Titel>Parameter</Titel>
            <Zahlfeld id="p-hoehe" label="Höhe über Start" einheit="m" wert={par.hoehe_m} min={10} max={500} onChange={v => setP({ hoehe_m: v ?? 60 })} />
            <Zahlfeld id="p-tempo" label="Geschwindigkeit" einheit="km/h" wert={ms_zu_kmh(par.tempo_ms)} min={TEMPO_MIN_KMH} max={TEMPO_MAX_KMH} schritt={1}
              onChange={v => setP({ tempo_ms: v == null ? 7 : Math.min(TEMPO_MAX_MS, v / 3.6) })} />
            {par.tempo_ms < TEMPO_MIN_MS && <p className="-mt-1 text-xs text-amber-400">Mindestens {TEMPO_MIN_KMH} km/h – es wird mit {TEMPO_MIN_KMH} km/h geflogen.</p>}
            <div className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm text-dark-text-muted">
              <span>Aufnahme</span>
              <div className="flex gap-1">
                {LINSEN.map(l => (
                  <Chip key={l.key} aktiv={par.linsen.includes(l.key)}
                    onClick={() => setP({ linsen: par.linsen.includes(l.key) ? (par.linsen.length > 1 ? par.linsen.filter(x => x !== l.key) : par.linsen) : [...par.linsen, l.key] })}>
                    {l.label}
                  </Chip>
                ))}
              </div>
            </div>
            {art !== 'punkte' && <>
              {typ !== 'FLAECHE' && (
                <div className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm text-dark-text-muted">
                  <span>Abstand nach</span>
                  <div className="flex gap-1">
                    {(['ir', 'wide'] as const).map(k => <Chip key={k} aktiv={par.abstand_sensor === k} onClick={() => setP({ abstand_sensor: k })}>{KAMERAS[k].name}</Chip>)}
                  </div>
                </div>
              )}
              <Zahlfeld id="p-ueberlappung" label="Seitliche Überlappung" einheit="%" wert={par.ueberlappung_seite} min={0} max={90} onChange={v => setP({ ueberlappung_seite: v ?? 20 })} />
              {typ === 'FLAECHE' && <Zahlfeld id="p-front" label="Überlappung vorn" einheit="%" wert={par.ueberlappung_front} min={0} max={90} onChange={v => setP({ ueberlappung_front: v ?? 70 })} />}
              <Zahlfeld id="p-abstand" label="Spurabstand (leer = auto)" einheit="m" wert={par.abstand_m} platzhalter={`${Math.round(kennzahlen?.abstand_m ?? spurabstand(par))}`} min={3} onChange={v => setP({ abstand_m: v })} />
            </>}
            {(art === 'flaeche' || art === 'mittelpunkt') && (
              <Zahlfeld id="p-richtung" label={art === 'flaeche' ? 'Bahnrichtung' : 'Erste Richtung'} einheit="°" wert={par.richtung_grad}
                platzhalter={art === 'flaeche' ? 'auto' : '0'} min={0} max={359} onChange={v => setP({ richtung_grad: v })} />
            )}
            {art === 'mittelpunkt' && <Zahlfeld id="p-radius" label="Suchradius" einheit="m" wert={par.radius_m} min={20} max={3000} onChange={v => setP({ radius_m: v ?? 300 })} />}
            {art === 'linie' && <>
              <Zahlfeld id="p-bahnen" label="Bahnen" wert={par.bahnen} min={1} max={9} onChange={v => setP({ bahnen: Math.max(1, Math.round(v ?? 1)) })} />
              <div className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm text-dark-text-muted">
                <span>Seite</span>
                <div className="flex gap-1">
                  {(['links', 'beide', 'rechts'] as Seite[]).map(s => <Chip key={s} aktiv={par.seite === s} onClick={() => setP({ seite: s })}>{s[0].toUpperCase() + s.slice(1)}</Chip>)}
                </div>
              </div>
            </>}
            <Zahlfeld id="p-gimbal" label="Gimbal-Neigung" einheit="°" wert={par.gimbal_pitch} min={-90} max={0} onChange={v => setP({ gimbal_pitch: v ?? -90 })} />
            <Zahlfeld id="p-akku" label="Nutzbare Zeit je Akku" einheit="min" wert={par.akku_min} min={5} max={60} onChange={v => setP({ akku_min: v ?? 30 })} />
          </div>

          <div className="space-y-1.5">
            <Titel>Berechnet</Titel>
            <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 rounded-lg border border-dark-border bg-dark-bg px-3 py-2 text-sm tabular-nums">
              <span className="text-dark-text-muted">Flugzeit</span><b className="text-dark-text">{kennzahlen ? `${zahl(kennzahlen.flugzeit_min, 0)} min` : '–'}</b>
              <span className="text-dark-text-muted">Akkus</span><b className={kennzahlen && kennzahlen.akkus > 1 ? 'text-amber-300' : 'text-dark-text'}>{kennzahlen?.akkus ?? '–'}</b>
              <span className="text-dark-text-muted">Strecke</span><b className="text-dark-text">{kennzahlen ? `${zahl(kennzahlen.strecke_m / 1000, 2)} km` : '–'}</b>
              {kennzahlen?.flaeche_ha !== null && kennzahlen?.flaeche_ha !== undefined && <><span className="text-dark-text-muted">Fläche</span><b className="text-dark-text">{zahl(kennzahlen.flaeche_ha, 1)} ha</b></>}
              {kennzahlen?.fotos ? <><span className="text-dark-text-muted">Fotos (ca.)</span><b className="text-dark-text">{kennzahlen.fotos}</b></> : null}
              <span className="text-dark-text-muted">Wegpunkte</span><b className="text-dark-text">{wegpunkte.length || '–'}</b>
            </div>
          </div>

          <div className="space-y-1.5">
            <Titel>Prüfung vor dem Senden</Titel>
            <ul className="space-y-1 text-sm">
              {autoPruefung.map((p, i) => (
                <li key={i} className="flex gap-2"><span className={p.ok ? 'text-emerald-400' : 'text-amber-300'}>{p.ok ? '✓' : '!'}</span><span className="text-dark-text">{p.text}</span></li>
              ))}
            </ul>
            {([['luftraum', 'Luftraum und Geozonen geprüft'], ['hindernisse', 'Hindernisse und Höhe geprüft'], ['wetter', 'Wetter und Wind geprüft']] as const).map(([k, text]) => (
              <label key={k} htmlFor={`pr-${k}`} className="flex min-h-[48px] items-center gap-3 text-sm text-dark-text">
                <input id={`pr-${k}`} type="checkbox" checked={pruefung[k]} onChange={e => aendern(setPruefung)({ ...pruefung, [k]: e.target.checked })} className="h-7 w-7" />
                {text}
              </label>
            ))}
          </div>

          {meldung && (
            <div className={`rounded-lg border px-3 py-2 text-sm ${meldung.fehler ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'}`}>{meldung.text}</div>
          )}

          <div className="mt-auto space-y-1.5">
            <Knopf primaer onClick={speichern} gesperrt={speichert || wegpunkte.length < 2}><Save className="h-4 w-4" /> {speichert ? 'Speichert …' : 'Speichern'}</Knopf>
            <Knopf onClick={kmzLaden} gesperrt={!kmzBereit}
              titel={kmzBereit ? undefined : 'Erst speichern und die drei Prüfpunkte abhaken'}>
              <Download className="h-4 w-4" /> KMZ für DJI Pilot 2
            </Knopf>
            <Knopf onClick={() => anPilot2(true)} gesperrt={!kmzBereit || pilot2Laeuft}
              titel={kmzBereit ? 'Route in die Routenbibliothek von DJI Pilot 2 stellen' : 'Erst speichern und die drei Prüfpunkte abhaken'}>
              <Send className="h-4 w-4" /> {pilot2Laeuft ? 'Sendet …' : pilot2Gesendet ? 'Erneut an Pilot 2 senden' : 'An Pilot 2 senden'}
            </Knopf>
            {routeId && pilot2Gesendet && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                <span>In der Routenbibliothek seit {new Date(pilot2Gesendet).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>
                <button type="button" onClick={() => anPilot2(false)} disabled={pilot2Laeuft} className="min-h-[32px] font-semibold underline disabled:opacity-40">Zurückziehen</button>
              </div>
            )}
            {routeId && (loeschenFrage ? (
              <div className="grid grid-cols-2 gap-1.5">
                <Knopf gefahr onClick={loeschen}>Wirklich löschen</Knopf>
                <Knopf onClick={() => setLoeschenFrage(false)}>Abbrechen</Knopf>
              </div>
            ) : (
              <Knopf gefahr onClick={() => setLoeschenFrage(true)}><Trash2 className="h-4 w-4" /> Route löschen</Knopf>
            ))}
            {geo.startplatz && wegpunkte.length >= 2 && (
              <p className="text-[11px] text-dark-text-subtle">Anflug vom Startplatz {zahl(abstandM(geo.startplatz, wegpunkte[0]))} m.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
